import { createContext, PropsWithChildren, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { AxiosError, AxiosResponse } from "axios";
import camelCase from "lodash/camelCase";
import mapKeys from "lodash/mapKeys";
import { GenericFallback } from "components/common/ErrorBoundaries";
import { services } from "services";
import { QueryAPIKey } from "utils/constants/api.constants";

type TTenantConfig = {
  [tenantName: string]: TTenantSpecificVars;
};

type TTenantSpecificVars = {
  API_URL: string;
  PLATFORM_TENANT: string;
  PLATFORM_TENANT_TYPE: string;
};

type TEnvVariablesConfigContextValue = {
  isLoading: boolean;
};

const EnvVariablesConfigContext = createContext<
  TEnvVariablesConfigContextValue | undefined
>(undefined);

export const useEnvVariablesConfig = (): TEnvVariablesConfigContextValue => {
  const context = useContext(EnvVariablesConfigContext);

  if (context === undefined) {
    throw new Error(
      "useEnvVariablesConfig must be used within an EnvVariablesConfigProvider",
    );
  }

  return context;
};

const EnvVariablesConfigProvider = (props: PropsWithChildren) => {
  const hostname = window.location.hostname;

  const { t } = useTranslation("common", { keyPrefix: "messages" });

  const setTenantSpecificVars = (tenantConfig: TTenantConfig | undefined) => {
    const tenantEnvVars = tenantConfig?.[hostname];

    if (!tenantEnvVars) {
      return;
    }

    const formattedTenantEnvVars = mapKeys(tenantEnvVars, (_, key) =>
      camelCase(key),
    );

    window.ENV = {
      ...window.ENV,
      ...formattedTenantEnvVars,
    };
  };

  const { isLoading, isError } = useQuery<
    AxiosResponse<TTenantConfig>,
    AxiosError
  >({
    queryKey: [QueryAPIKey.GetTenantSpecificVariables],
    queryFn: () =>
      services.env.getConfig().then((response) => {
        setTenantSpecificVars(response?.data);

        return response;
      }),
    enabled: hostname.includes("-dev"),
    staleTime: 0,
  });

  const value = useMemo(() => ({ isLoading }), [isLoading]);

  // Will be implemented in scope of FTE-30793
  if (isLoading) {
    return null;
  }

  if (isError) {
    return <GenericFallback message={t("genericError")} />;
  }

  return (
    <EnvVariablesConfigContext.Provider value={value}>
      {props.children}
    </EnvVariablesConfigContext.Provider>
  );
};

export default EnvVariablesConfigProvider;
